import { getApiKey } from '@/lib/api-keys';
import { callProxy, isProxyConfigured } from '@/lib/proxy';
import { tryConsume } from '@/lib/quota';

/**
 * Google Vision DOCUMENT_TEXT_DETECTION wrapper + receipt parser.
 *
 * Two paths:
 *  - If the user has set their own Vision key in Settings → Admin → API
 *    keys, we call Google directly (lets devs / power users override).
 *  - Otherwise we route through the Supabase Edge Function (`google-proxy`)
 *    so the shipped app works without users bringing their own key.
 *
 * The caller is responsible for grabbing base64 — easiest path is to ask
 * expo-image-picker for it inline: `launchCameraAsync({ base64: true })`,
 * then pass `asset.base64` here.
 */

export interface ParsedReceipt {
  rawText: string;
  vendor: string | null;
  /** Best-guess item / line-item description (e.g. "Improved AJAX Login & Register"). */
  description: string | null;
  amount_cents: number | null;
  date: number | null;
  lines: string[];
  /** Product label extras (Wave 49) — drive the inventory branch of
   *  Smart Scan. Null when nothing model/serial-shaped is present. */
  model: string | null;
  serial: string | null;
  /** Wave 53 richer extraction — every estimate/invoice in the user's
   *  test pack carried these, and the forms were leaving them blank. */
  /** "Project: Front foundation bed refresh…" — estimates/quotes. */
  project: string | null;
  /** "Valid through 10/02/2026" / "Quote valid for 30 days" → epoch ms. */
  valid_until: number | null;
  /** Document number ("INV-10492", "EST-260731", "Receipt #R-82641"). */
  doc_number: string | null;
  /** Itemized work/product lines with their amounts where visible. */
  line_items: { desc: string; amount_cents: number | null }[];
}

export type OcrError =
  | { kind: 'no-key' }
  | { kind: 'network'; message: string }
  | { kind: 'vision'; message: string };

export type OcrResult =
  | { ok: true; parsed: ParsedReceipt }
  | { ok: false; error: OcrError };

const VISION_URL = 'https://vision.googleapis.com/v1/images:annotate';

/**
 * Shared Vision OCR call — key/proxy routing + quota + full-text
 * extraction. Every scanner (receipt, estimate, business card, smart
 * scan) funnels through here so the budget + error handling stay in
 * one place.
 */
async function visionTextBase64(
  base64: string
): Promise<{ ok: true; text: string } | { ok: false; error: OcrError }> {
  const userKey = await getApiKey('google_vision');
  if (!userKey && !isProxyConfigured()) return { ok: false, error: { kind: 'no-key' } };

  const budget = await tryConsume('vision');
  if (!budget.ok) {
    return {
      ok: false,
      error: {
        kind: 'vision',
        message: `Daily Vision call ceiling hit (${budget.ceiling}). Resets at midnight UTC — protects the Google bill.`,
      },
    };
  }

  let payload: unknown;
  if (userKey) {
    try {
      const res = await fetch(`${VISION_URL}?key=${encodeURIComponent(userKey)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requests: [{
            image: { content: base64 },
            features: [{ type: 'DOCUMENT_TEXT_DETECTION', maxResults: 1 }],
          }],
        }),
      });
      payload = await res.json();
      if (!res.ok) {
        const msg = extractVisionError(payload) ?? `HTTP ${res.status}`;
        return { ok: false, error: { kind: 'vision', message: msg } };
      }
    } catch (err) {
      return { ok: false, error: { kind: 'network', message: String(err) } };
    }
  } else {
    const proxied = await callProxy<unknown>({ service: 'vision', base64 });
    if (!proxied.ok) {
      const kind = proxied.error.kind === 'network' ? 'network' : 'vision';
      return { ok: false, error: { kind, message: proxied.error.message } };
    }
    payload = proxied.payload;
  }

  const text = extractFullText(payload);
  if (!text) {
    return {
      ok: false,
      error: { kind: 'vision', message: 'No text detected. Try a clearer photo.' },
    };
  }
  return { ok: true, text };
}

export async function scanReceiptBase64(base64: string): Promise<OcrResult> {
  const res = await visionTextBase64(base64);
  if (!res.ok) return res;
  return { ok: true, parsed: parseReceiptText(res.text) };
}

/**
 * PDF OCR (Wave 51) via Vision `files:annotate` — synchronous for
 * inline content, first 5 pages (every real quote/invoice fits).
 * Same key/proxy routing and quota as image scans.
 */
export async function scanPdfBase64(base64: string): Promise<OcrResult> {
  const userKey = await getApiKey('google_vision');
  if (!userKey && !isProxyConfigured()) return { ok: false, error: { kind: 'no-key' } };

  const budget = await tryConsume('vision');
  if (!budget.ok) {
    return {
      ok: false,
      error: {
        kind: 'vision',
        message: `Daily Vision call ceiling hit (${budget.ceiling}). Resets at midnight UTC — protects the Google bill.`,
      },
    };
  }

  let payload: unknown;
  if (userKey) {
    try {
      const res = await fetch(
        `https://vision.googleapis.com/v1/files:annotate?key=${encodeURIComponent(userKey)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requests: [{
              inputConfig: { content: base64, mimeType: 'application/pdf' },
              features: [{ type: 'DOCUMENT_TEXT_DETECTION' }],
              pages: [1, 2, 3, 4, 5],
            }],
          }),
        }
      );
      payload = await res.json();
      if (!res.ok) {
        const msg = extractVisionError(payload) ?? `HTTP ${res.status}`;
        return { ok: false, error: { kind: 'vision', message: msg } };
      }
    } catch (err) {
      return { ok: false, error: { kind: 'network', message: String(err) } };
    }
  } else {
    const proxied = await callProxy<unknown>({ service: 'vision-pdf', base64 });
    if (!proxied.ok) {
      const kind = proxied.error.kind === 'network' ? 'network' : 'vision';
      return { ok: false, error: { kind, message: proxied.error.message } };
    }
    payload = proxied.payload;
  }

  const text = extractPdfFullText(payload);
  if (!text) {
    return {
      ok: false,
      error: { kind: 'vision', message: 'No text detected in the PDF.' },
    };
  }
  return { ok: true, parsed: parseReceiptText(text) };
}

/** files:annotate nests per-file → per-page responses; concatenate the
 *  page texts in order. */
function extractPdfFullText(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const files = (payload as Record<string, unknown>).responses;
  if (!Array.isArray(files) || files.length === 0) return null;
  const pages = (files[0] as Record<string, unknown>).responses;
  if (!Array.isArray(pages)) return null;
  const parts: string[] = [];
  for (const p of pages) {
    const ann = (p as Record<string, unknown>).fullTextAnnotation as
      | Record<string, unknown>
      | undefined;
    if (ann && typeof ann.text === 'string') parts.push(ann.text);
  }
  return parts.length > 0 ? parts.join('\n') : null;
}

// --- business cards ---------------------------------------------------------

export interface ParsedBusinessCard {
  rawText: string;
  name: string | null;
  company: string | null;
  role: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
}

export type CardOcrResult =
  | { ok: true; parsed: ParsedBusinessCard }
  | { ok: false; error: OcrError };

export async function scanBusinessCardBase64(base64: string): Promise<CardOcrResult> {
  const res = await visionTextBase64(base64);
  if (!res.ok) return res;
  return { ok: true, parsed: parseBusinessCardText(res.text) };
}

const CARD_PHONE_RE = /(\+?1[\s.\-–]?)?\(?\d{3}\)?[\s.\-–]?\d{3}[\s.\-–]?\d{4}\b/;
const CARD_EMAIL_RE = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/;
const CARD_SITE_RE = /\b(?:https?:\/\/)?(?:www\.)?[\w-]+\.(?:com|net|org|biz|us|co|io|services|pro|contractors|construction)\b[^\s,]*/i;
const CARD_COMPANY_HINT =
  /\b(llc|l\.l\.c\.|inc\.?|corp\.?|co\.?|company|ltd|services?|solutions|contracting|contractors?|construction|builders?|roofing|plumbing|heating|cooling|hvac|electric(al)?|landscap\w*|painting|remodel\w*|restoration|exteriors?|interiors?|handyman|maintenance|septic|paving|masonry|concrete|tree|lawn|pest|cleaning|inspection)\b/i;
const CARD_ROLE_HINT =
  /\b(owner|president|founder|manager|estimator|technician|foreman|supervisor|specialist|consultant|agent|realtor|broker|inspector|master\s+(plumber|electrician)|licensed|sales|service|project\s+manager|operations|ceo|coo|vp)\b/i;

/**
 * Business-card text → structured contact fields. Cards are much less
 * structured than receipts, so each field uses its strongest available
 * signal and returns null rather than guessing:
 *   - email/phone/website: regex (highest confidence)
 *   - company: line with a trade/entity hint, else the ALL-CAPS line
 *   - name: 2-3 capitalized words, no digits, not the company line
 *   - role: line with a title keyword that isn't the name/company line
 */
export function parseBusinessCardText(text: string): ParsedBusinessCard {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter((l) => l.length > 0);

  const email = text.match(CARD_EMAIL_RE)?.[0] ?? null;
  const phone = text.match(CARD_PHONE_RE)?.[0]?.trim() ?? null;
  // Website: first domain-looking token that isn't part of the email.
  let website: string | null = null;
  for (const l of lines) {
    const m = l.match(CARD_SITE_RE)?.[0];
    if (m && !(email && email.includes(m.replace(/^https?:\/\//i, '')))) {
      website = m.replace(/[.,;]$/, '');
      break;
    }
  }

  const isContactLine = (l: string) =>
    CARD_EMAIL_RE.test(l) || CARD_PHONE_RE.test(l) || CARD_SITE_RE.test(l) ||
    /\d{5}(-\d{4})?\s*$/.test(l) || /^\d+\s+\S/.test(l);

  // Company: strongest = trade/entity keyword; fallback = an ALL-CAPS
  // multi-char line near the top (logos OCR as caps).
  let company: string | null = null;
  for (const l of lines) {
    if (isContactLine(l)) continue;
    if (CARD_COMPANY_HINT.test(l) && !CARD_ROLE_HINT.test(l)) {
      company = l.slice(0, 60);
      break;
    }
  }
  if (!company) {
    company =
      lines.find(
        (l) =>
          !isContactLine(l) &&
          l.length >= 6 &&
          l.length <= 40 &&
          l === l.toUpperCase() &&
          /[A-Z]{4,}/.test(l)
      )?.slice(0, 60) ?? null;
  }

  // Name: 2-3 words, each starting uppercase, mostly letters, and not
  // the line we already called the company.
  let name: string | null = null;
  for (const l of lines) {
    if (l === company || isContactLine(l)) continue;
    if (CARD_COMPANY_HINT.test(l)) continue;
    const words = l.split(' ');
    if (words.length < 2 || words.length > 3) continue;
    if (/\d/.test(l)) continue;
    if (!words.every((w) => /^[A-Z][A-Za-z.'-]*$/.test(w))) continue;
    if (CARD_ROLE_HINT.test(l)) continue;
    name = l.slice(0, 60);
    break;
  }

  let role: string | null = null;
  for (const l of lines) {
    if (l === company || l === name || isContactLine(l)) continue;
    if (CARD_ROLE_HINT.test(l) && l.length <= 40) {
      role = l.slice(0, 40);
      break;
    }
  }

  return { rawText: text, name, company, role, phone, email, website };
}

// --- smart classification ---------------------------------------------------

export type ScanDocKind = 'quote' | 'expense';

/** Full Smart Scan classification (Wave 49). Money paths open pre-filled
 *  forms; document kinds file straight into Media & Files; 'inventory'
 *  opens the inventory form; null = ask the user. */
export type SmartScanKind =
  | 'quote'
  | 'expense'
  | 'inventory'
  | 'manual'
  | 'warranty'
  | 'insurance'
  | 'permit'
  | 'inspection'
  | null;

/**
 * Classify a scanned document as a QUOTE (estimate/bid/proposal — money
 * you MIGHT spend) vs an EXPENSE (receipt/invoice/bill — money spent or
 * owed). Drives the Smart Scan router: one camera button, and the app
 * files the paper in the right place.
 *
 * Signal: estimate-family words near the top of the document, or
 * anywhere when paired with "valid for/until" language. Invoices and
 * receipts default to expense — an invoice is a bill for work done.
 */
export function classifyScanKind(parsed: ParsedReceipt): ScanDocKind {
  const head = parsed.lines.slice(0, 10).join(' ');
  const QUOTE_WORDS = /\b(estimate|estimated|quotation|quote|proposal|bid)\b/i;
  if (QUOTE_WORDS.test(head)) return 'quote';
  if (QUOTE_WORDS.test(parsed.rawText) && /\bvalid\s+(for|until|thru|through)\b/i.test(parsed.rawText)) {
    return 'quote';
  }
  return 'expense';
}

/**
 * Extended classifier: money documents (quote/expense), product labels
 * (inventory), and paper-record kinds that belong in Media & Files.
 * Order matters — quote words beat everything (an estimate PDF also
 * says "warranty" half the time), document-kind words beat the money
 * default, and a model/serial plate with NO dollar amounts reads as a
 * product label → inventory. Returns null when nothing is confident,
 * so the UI can ask instead of guessing.
 */
export function classifySmartScan(parsed: ParsedReceipt): SmartScanKind {
  // Negation guard (Wave 56): "This plan is NOT insurance and NOT a
  // warranty" must not classify as insurance. Strip negated kind
  // words before the keyword checks.
  const stripNegated = (s: string) =>
    s.replace(/\bnot\s+(an?\s+)?(insurance|warranty|invoice|estimate|quote|permit)\b/gi, ' ');
  const head = stripNegated(parsed.lines.slice(0, 12).join(' '));
  const all = stripNegated(parsed.rawText);

  // Rotated / mirrored scan detector (Wave 54.1): OCR of an
  // upside-down receipt yields reversed words — "Subtotal" becomes
  // "latotduS". If money words match the REVERSED text better than the
  // forward text, no auto-filing: hand it to the human chooser instead
  // of creating a junk record.
  const MONEY_WORDS = /subtotal|total|invoice|receipt|amount|payment|balance/gi;
  const fwdHits = (all.match(MONEY_WORDS) ?? []).length;
  const revText = all.split('').reverse().join('');
  const revHits = (revText.match(MONEY_WORDS) ?? []).length;
  if (revHits > fwdHits) return null;

  // Paper-record kinds FIRST when the head announces the document type
  // (Wave 53: an "INSPECTION REPORT" that says "estimated 8-12 years
  // old" inside was landing as a quote). Strong phrases only.
  if (
    /\b(owner'?s?[\s&]{0,8}(installation\s+)?manual|user\s+(manual|guide)|instruction(s|\s+manual)|installation\s+(guide|manual)|operating\s+instructions|operation\s+(guide|manual))\b/i.test(
      head
    )
  ) {
    return 'manual';
  }
  if (/\b(certificate\s+of\s+warranty|limited\s+warranty|product\s+warranty|warranty\s+(certificate|registration|card))\b|warranty\s*#/i.test(head)) {
    return 'warranty';
  }
  // Header split across OCR lines ("LIMITED / PRODUCT / WARRANTY"):
  // a bare "warranty" in the head still wins when the body reads like
  // a coverage document rather than a sales slip.
  if (
    /\bwarranty\b/i.test(head) &&
    (/\b(coverage\s+(term|summary)|registered\s+owner|this\s+warranty\s+(covers|does\s+not))\b/i.test(all) ||
      /\b(registration|confirmation|coverage)\b/i.test(head))
  ) {
    return 'warranty';
  }
  // Bare "coverage" was dropped from the trigger set (Wave 56): an
  // HVAC maintenance plan says "Coverage period" without being
  // insurance. Real declarations pages always carry a stronger word.
  if (/\b(insurance|policy\s+(no|number)|declarations?(\s+page)?|premium|deductible)\b/i.test(head) && /\b(policy|insur|coverage)\b/i.test(all)) {
    return 'insurance';
  }
  if (/\b(building\s+permit|permit\s+(no|number|#)|certificate\s+of\s+occupancy|zoning)\b/i.test(head)) {
    return 'permit';
  }
  if (
    /\b(inspection\s+(report|record|summary)|home\s+inspection|(condition|assessment)\s+report|test\s+report|lab(oratory)?\s+(report|results?|analysis)|water\s+analysis|radon)\b/i.test(
      head
    )
  ) {
    return 'inspection';
  }

  const QUOTE_WORDS = /\b(estimate|estimated|quotation|quote|proposal|bid)\b/i;
  if (QUOTE_WORDS.test(head)) return 'quote';
  if (QUOTE_WORDS.test(all) && /\bvalid\s+(for|until|thru|through)\b/i.test(all)) {
    return 'quote';
  }

  // Product label: model/serial present and no meaningful money total
  // (spec plates carry stray small numbers — "3.5 IN WC" — that the
  // money scanner can misread as an amount).
  if (
    (parsed.model || parsed.serial) &&
    (parsed.amount_cents == null || parsed.amount_cents < 2000)
  ) {
    return 'inventory';
  }

  // Money present → a receipt / invoice / bill. Trivial amounts (<$1)
  // are OCR noise, not purchases.
  if (parsed.amount_cents != null && parsed.amount_cents >= 100) return 'expense';

  return null;
}

/**
 * For a money document: is this PROOF OF PAYMENT (→ file as a receipt)
 * or an OPEN BILL (→ file as an invoice)? A paid invoice is a receipt
 * in spirit — "PAID", "payment received", or a zero amount-due settles
 * it; an invoice still carrying due-language stays an invoice.
 */
export function classifyPaidStatus(parsed: ParsedReceipt): 'receipt' | 'invoice' {
  // Negation guard (Wave 53.1): "this invoice has NOT been paid in
  // full" must not read as paid. Strip negated phrases first.
  const t = parsed.rawText.replace(
    /\b(?:has\s+)?not\s+(?:yet\s+)?been\s+paid[^.\n]*|\bnot\s+paid\b[^.\n]*|\bunpaid\b/gi,
    ''
  );
  // 1. Unambiguous proof of payment: "PAID IN FULL", "Status: PAID",
  //    or a zero balance. These beat everything.
  if (
    /\bpaid\s+in\s+full\b|\bstatus[:\s]+paid\b|payment\s+received\s+in\s+full|\b(amount|balance)\s+due\b[:\s]*[-–]?\s*\$?\s*0+(\.0{1,2})?(\s|$)/i.test(
      t
    )
  ) {
    return 'receipt';
  }
  // 2. A live amount due WITH due-date/terms language is an open bill —
  //    even when the statement also lists "payments received" for last
  //    month (utility bills always do; Wave 54.1).
  if (
    /\b(amount|balance)\s+due\b/i.test(t) &&
    /\bdue\s+(date|by|on|upon)\b|\bnet\s*\d+\b|please\s+remit/i.test(t)
  ) {
    return 'invoice';
  }
  // 2b. A NONZERO amount due on its own line is an open bill even
  //     without due-date wording — "AMOUNT DUE $241.86" next to
  //     "Payment received 08/20" (last month's payment) still means
  //     money is owed.
  if (/\b(amount|balance)\s+due\b[^\n$]*\$\s*[1-9]/i.test(t)) {
    return 'invoice';
  }
  // 3. Generic paid markers (register receipts, "PAID" stamps).
  if (/\bpaid\b|payment\s+received/i.test(t)) {
    return 'receipt';
  }
  if (
    /\binvoice\b/i.test(t) &&
    /\b(amount\s+due|balance\s+due|due\s+(date|upon)|net\s*\d+|please\s+remit)\b/i.test(t)
  ) {
    return 'invoice';
  }
  // Register receipts, store slips, anything without due-language.
  return 'receipt';
}

function extractVisionError(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const err = (p.error as Record<string, unknown> | undefined)?.message;
  if (typeof err === 'string') return err;
  const responses = p.responses as Array<Record<string, unknown>> | undefined;
  const first = responses?.[0];
  const inner = (first?.error as Record<string, unknown> | undefined)?.message;
  return typeof inner === 'string' ? inner : null;
}

function extractFullText(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const responses = (payload as Record<string, unknown>).responses;
  if (!Array.isArray(responses) || responses.length === 0) return null;
  const first = responses[0] as Record<string, unknown>;
  const ann = first.fullTextAnnotation as Record<string, unknown> | undefined;
  if (ann && typeof ann.text === 'string') return ann.text;
  const text = first.textAnnotations as Array<Record<string, unknown>> | undefined;
  const desc = text?.[0]?.description;
  return typeof desc === 'string' ? desc : null;
}

// --- parsing ---------------------------------------------------------------

const TOTAL_HINTS = /\b(total|amount due|balance due|grand total|amount)\b/i;
const SUBTOTAL_BLOCK = /\b(subtotal|sub-total|tax|change|tendered|cash|visa|mastercard|debit|credit|tip|gratuity|due)\b/i;
const MONEY_RE = /(?:\$\s?)?(\d{1,4}(?:,\d{3})*\.\d{2})/g;
const DATE_RES: RegExp[] = [
  /\b(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})\b/,
  /\b(20\d{2})[/\-.](\d{1,2})[/\-.](\d{1,2})\b/,
  /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(\d{1,2}),?\s+(20\d{2})\b/i,
];

export function parseReceiptText(text: string): ParsedReceipt {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const lineItems = detectLineItems(lines);
  const project = detectProject(lines);
  // A "document date" more than a week in the future is an OCR misread
  // or a reminder date ("Next service: 06/05/2028") — receipts and
  // invoices are dated in the past. valid_until keeps its own future
  // dates; this only guards the document date.
  let docDate = detectDate(text);
  if (docDate != null && docDate > Date.now() + 7 * 24 * 60 * 60 * 1000) docDate = null;
  return {
    rawText: text,
    vendor: detectVendor(lines),
    description: detectDescription(lines, lineItems, project),
    amount_cents: detectAmountCents(lines),
    date: docDate,
    lines,
    // Word-boundary after the label + a mandatory separator — "ser"
    // must never match inside "Services" (Wave 54.1: a septic sketch
    // mentioning "Clearwater Septic Services" became an inventory item).
    model: detectLabeledCode(text, /\b(?:model|mod|m\/n)\b[\s.:#]*(?:no|number|#)?[\s.:#]+([A-Z0-9][A-Z0-9\/.-]{2,24})/i),
    serial: detectLabeledCode(text, /\b(?:serial|ser|s\/n|sn)\b[\s.:#]*(?:no|number|#)?[\s.:#]+([A-Z0-9][A-Z0-9\/.-]{4,30})/i),
    project,
    valid_until: detectValidUntil(text),
    doc_number: detectDocNumber(text),
    line_items: lineItems,
  };
}

/** "Project: Front foundation bed refresh and low-voltage landscape
 *  lighting" — estimates and quotes name the job; the quote form has a
 *  Project field that was being left blank. */
function detectProject(lines: string[]): string | null {
  for (const line of lines) {
    // "Project: …" and the vet/contractor variant "Estimate Title: …"
    // both name the job directly — direct copy beats any heuristic.
    const m = /^(?:project|estimate\s+title|job(?:\s+name)?)\s*[:\-]\s*(.{2,120})$/i.exec(line);
    if (m) {
      const v = m[1].trim().replace(/\s+/g, ' ');
      if (v.replace(/[^a-z]/gi, '').length >= 4) return v.slice(0, 80);
    }
  }
  return null;
}

/** "Valid through: 10/02/2026", "Valid until 10/2/26", or relative
 *  "Quote valid for 30 days" (anchored on the document date). */
function detectValidUntil(text: string): number | null {
  const abs = /\bvalid\s*(?:through|until|thru|to)\s*[:\-]?\s*([0-9a-z ,/.\-]{6,20})/i.exec(text);
  if (abs) {
    const d = detectDate(abs[1]);
    if (d != null) return d;
  }
  const rel = /\bvalid\s+for\s+(\d{1,3})\s+days?\b/i.exec(text);
  if (rel) {
    const base = detectDate(text);
    if (base != null) return base + Number(rel[1]) * 24 * 60 * 60 * 1000;
  }
  const expires = /\b(?:expires?|expiration)\s*[:\-]?\s*([0-9a-z ,/.\-]{6,20})/i.exec(text);
  if (expires) {
    const d = detectDate(expires[1]);
    if (d != null) return d;
  }
  return null;
}

/** "Receipt #R-82641", "INV-10492", "EST-260731", "#GL-55319",
 *  "Warranty #AT-WH-260844", "Receipt: 004281". */
function detectDocNumber(text: string): string | null {
  // Three shapes: "Receipt: 004281" (colon), "Warranty #AT-WH-260844"
  // (hash), "invoice INV-10492" / "QUOTE Q-66184" (bare letter-dash
  // code). Iterate all label hits — the first digit-bearing code wins,
  // so a digit-less capture ("Workmanship") or a street address after
  // a bare "INVOICE" header never poisons the result.
  const labeledRe =
    /\b(?:receipt|invoice|estimate|quote|order|warranty|policy|claim|ref(?:erence)?)\s*(?:no|number|num)?\s*(?:[:#]\s*#?\s*([A-Z0-9][A-Z0-9-]{2,24})|#\s*([A-Z0-9][A-Z0-9-]{2,24})|\s+([A-Z]{1,6}-[A-Z0-9-]{2,24}))/gi;
  let m: RegExpExecArray | null;
  while ((m = labeledRe.exec(text))) {
    const code = (m[1] || m[2] || m[3] || '').trim();
    if (code && /\d/.test(code) && !/^\d{1,2}$/.test(code)) return code.slice(0, 30);
  }
  const codedRe = /(?:^|\s)#?\b([A-Z]{1,4}-[A-Z0-9][A-Z0-9-]{2,20})\b/gm;
  while ((m = codedRe.exec(text))) {
    if (/\d/.test(m[1])) return m[1].slice(0, 30);
  }
  return null;
}

// Lines that are money-flow chrome, never work items.
const LINE_ITEM_SKIP =
  /\b(subtotal|sub-total|total|tax|amount\s+due|past\s+due|balance|payment|paid|visa|mastercard|amex|discover|debit|credit|cash|change|tendered|contingency|allowance|deposit|discount|network\s*id|appr(oval)?\s*code|auth\s*code)\b/i;

/**
 * Itemized lines: "Install whole-home surge protector at main panel  1
 * $285.00 $285.00" → { desc, amount_cents }. Feeds the notes summary
 * and the "top item + N more" title. Amount = LAST money value on the
 * line (the extended amount in qty/rate/amount tables).
 */
function detectLineItems(lines: string[]): { desc: string; amount_cents: number | null }[] {
  const items: { desc: string; amount_cents: number | null }[] = [];
  for (const line of lines) {
    if (LINE_ITEM_SKIP.test(line)) continue;
    const money = [...line.matchAll(/\$\s?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/g)];
    if (money.length === 0) continue;
    const desc = line
      .slice(0, money[0].index)
      .replace(/\s+\d+(\.\d+)?\s*(x|@|hrs?|hr|ea|yd|ft|project)?\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
    const letters = desc.replace(/[^a-z]/gi, '');
    if (letters.length < 5) continue;
    // A desc ending in ':' is a label ("Optional annual maintenance:"),
    // and optional add-ons aren't the purchased work.
    if (/:\s*$/.test(desc) || /^optional\b/i.test(desc)) continue;
    // Column-header rows ("Work performed  Hours/Qty  Unit price
    // Amount") — never real items regardless of length.
    if (/^(description|items?|services?|labou?r|work\s+performed|estimated\s+scope|qty|quantity)\b\s*($|hours?|qty|quantity|unit|rate|price|amount|\/)/i.test(desc)) continue;
    if (/\b(hours?\s*\/\s*qty|unit\s+price)\b/i.test(desc)) continue;
    const last = money[money.length - 1][1];
    items.push({
      desc: desc.slice(0, 80),
      amount_cents: Math.round(parseFloat(last.replace(/,/g, '')) * 100),
    });
    if (items.length >= 12) break;
  }
  if (items.length > 0) return items;

  // Register-receipt layout (Vision OCR splits columns onto separate
  // lines): a product line followed by "2 @ 12.98". The extended
  // amounts land elsewhere in the OCR stream, so amount stays null —
  // the item names are what the notes and title need.
  for (let i = 0; i < lines.length - 1; i++) {
    const line = lines[i];
    if (LINE_ITEM_SKIP.test(line)) continue;
    if (/[$@]|thank|receipt|customer|register|cashier|return/i.test(line)) continue;
    if (/^\d/.test(line)) continue;
    if (line.replace(/[^a-z]/gi, '').length < 5) continue;
    if (!/^\d+(\.\d+)?\s*@\s*\d/.test(lines[i + 1])) continue;
    items.push({ desc: line.replace(/\s+/g, ' ').slice(0, 80), amount_cents: null });
    if (items.length >= 12) break;
  }
  if (items.length > 0) return items;

  // Warehouse-club / SKU-prefixed layout ("33249 RASPBERRIESF",
  // "990004047 ICEE VARIETE") — the item name follows a numeric code.
  // Amounts live in a separate OCR column, so they stay null; the
  // names alone make the title and notes readable (Wave 55).
  for (const line of lines) {
    const m = /^\d{4,13}\s+([A-Z][A-Z0-9 &'./-]{4,40})$/.exec(line.trim());
    if (!m) continue;
    const desc = m[1].replace(/\s+/g, ' ').trim();
    if (LINE_ITEM_SKIP.test(desc)) continue;
    // Street addresses match the SKU shape too ("6385 SEYMOUR RD").
    if (/\b(rd|st|ave|dr|ln|ct|blvd|hwy|road|street|avenue|drive|lane|court|highway|suite|ste)\b\.?$/i.test(desc)) continue;
    items.push({ desc: desc.slice(0, 80), amount_cents: null });
    if (items.length >= 12) break;
  }
  return items;
}

/**
 * Never-empty record title (Wave 53). Priority:
 *   1. multi-item docs → "Top item + N more" (summarizes the work)
 *   2. description / project line
 *   3. "{Vendor} — {kind}" (vendor alone is the VENDOR field's job)
 *   4. the caller's fallback ("Receipt — Sep 7, 2026")
 */
export function composeScanTitle(
  parsed: ParsedReceipt | null,
  kindLabel: string,
  fallback: string
): string {
  if (!parsed) return fallback;
  if (parsed.project?.trim()) return parsed.project.trim().slice(0, 80);
  const items = parsed.line_items ?? [];
  if (items.length >= 2) {
    const top = [...items].sort((a, b) => (b.amount_cents ?? 0) - (a.amount_cents ?? 0))[0];
    return `${top.desc} + ${items.length - 1} more`.slice(0, 80);
  }
  const desc = parsed.description?.trim();
  if (desc) return desc.slice(0, 80);
  // The store name alone beats any synthetic label — "Sam's Club" is a
  // better title than "Receipt — 9/7/2026" (Wave 55, user call).
  if (parsed.vendor?.trim()) return parsed.vendor.trim().slice(0, 80);
  return fallback;
}

/**
 * Searchable notes block (Wave 53): doc number, line items with
 * amounts, and any terms / warranty / validity / next-service lines —
 * so "surge protector" or "INV-10492" finds the record later without
 * opening the file. Capped so notes stay a note, not a dump.
 */
export function composeScanNotes(parsed: ParsedReceipt | null): string | null {
  if (!parsed) return null;
  const out: string[] = [];
  if (parsed.doc_number) out.push(`Doc #: ${parsed.doc_number}`);
  for (const it of (parsed.line_items ?? []).slice(0, 8)) {
    out.push(
      it.amount_cents != null
        ? `• ${it.desc} — $${(it.amount_cents / 100).toFixed(2)}`
        : `• ${it.desc}`
    );
  }
  const extraRe =
    /\b(payment terms|terms|warranty|valid (?:through|until|for)|next (?:annual|recommended)?\s*(?:service|inspection)|replace filter|recommended maintenance|coverage term)\b/i;
  for (const line of parsed.lines) {
    if (out.length >= 14) break;
    if (extraRe.test(line) && line.length <= 140 && !out.includes(line)) out.push(line);
  }
  if (out.length === 0) return null;
  const joined = out.join('\n');
  return joined.length > 800 ? joined.slice(0, 800) : joined;
}

/** Extract a labeled alphanumeric code ("Model No: ABC-123"). Rejects
 *  captures that are pure label-words the regex accidentally grabbed. */
function detectLabeledCode(text: string, re: RegExp): string | null {
  const m = re.exec(text);
  if (!m) return null;
  const code = m[1].trim().replace(/[.,;]+$/, '');
  if (/^(no|number)$/i.test(code)) return null;
  return code.slice(0, 30);
}

// Lines that look like document chrome rather than the actual store name.
// Invoices (vs cash receipts) often lead with "INVOICE", "Order ID:",
// "Paid In Full :", and section headers like "Seller" / "Purchaser" before
// the real vendor name shows up. We skip them to land on a real name.
const VENDOR_SKIP_PREFIX =
  /^(receipt|order|invoice|store|date|time|cashier|register|tel|phone|fax|www|http|paid|seller|purchaser|customer|client|buyer|patient|caregiver|member|insured|billed|bill\s*to|ship\s*to|sold\s*to|qty|quantity|description|item|product|subtotal|sub-total|total|amount|sales\s*tax|tax|due|balance|tendered|change|payment|method|terms|po\s*#|po\s*number|invoice\s*#|page|doc|document|merchant|account|membership|reference|ref\s*#|transaction|trans|auth|approval|terminal|lane|cust|copy|original|request|status|type|category|kind|code|id\b|number|no\.?\s|year|month|day|notes?|comments?)/i;

// Standalone single-word column headers we should never pick as a vendor
// or description. Vision OCR loves to extract these from tabular receipts
// (e.g. "Request" / "Year" / "Status" as a column heading) and they end
// up as the highest-scoring single-word "candidate" if we let them.
const SINGLE_WORD_HEADER = new Set([
  'request','year','month','day','date','time','status','type','code',
  'category','kind','reference','ref','total','subtotal','tax','amount',
  'price','qty','quantity','number','no','id','name','title','description',
  'item','product','details','service','notes','comments','memo','from',
  'to','vendor','customer','order','receipt','invoice','page','page#',
  'paid','due','balance','change','cash','credit','debit','visa','mastercard',
  'discover','amex','american','express','approved','approval','auth',
  'terminal','lane','register','cashier','clerk','staff','employee','tip',
  'gratuity','tendered','rendered','company','co','inc','llc','corp','ltd',
]);

/**
 * Rotated / handwritten / mirrored scans OCR into letter salad
 * ("ияитая ЖИАНТ", "Englalegu sɑuqacɑbs"). A real US home document is
 * overwhelmingly ASCII — reject candidates that aren't, rather than
 * planting gibberish in a title (Wave 53 stress pack, docs 01 + 15).
 */
function looksGibberish(s: string): boolean {
  const letters = s.match(/\p{L}/gu) ?? [];
  if (letters.length < 4) return false;
  const nonAscii = letters.filter((c) => c.charCodeAt(0) > 127).length;
  if (nonAscii / letters.length > 0.2) return true;
  // Mirrored/rotated scans also produce digit-letter mash words
  // ("29919bia", "boi19") — two or more of those is OCR salad.
  const words = s.split(/\s+/);
  let mashed = 0;
  for (const w of words) {
    if (/\d{2,}/.test(w) && /[a-z]{2,}/i.test(w) && !/^\d+(x|mm|cm|in|ft|hp|kw|v|w|gal|yr|pk)$/i.test(w)) {
      mashed++;
    }
  }
  return mashed >= 2;
}

function isLikelyHeaderToken(raw: string): boolean {
  const s = raw.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!s) return true;
  return SINGLE_WORD_HEADER.has(s);
}

/**
 * True when the FIRST word of a multi-word line is a known column header.
 * Catches OCR outputs like "Request Code ABC123" or "Year Built 1985" or
 * "Order # 4927" that the single-word check misses.
 *
 * Be conservative with first words that are also legitimate brand prefixes
 * — we whitelist canonical store names elsewhere, so leaving "Apple Cash"
 * out is fine here. Tokens kept short-and-strict to avoid false rejects.
 */
const FIRST_WORD_HEADERS = new Set([
  'request','year','month','day','date','time','status','type','code',
  'category','reference','ref','order','receipt','invoice','page','total',
  'subtotal','tax','amount','price','qty','quantity','customer','client',
  'seller','purchaser','buyer','billed','bill','ship','sold','vendor',
  'paid','due','balance','auth','approval','approved','terminal','register',
  'cashier','employee','tendered','change','tip','gratuity','description',
  'item','product','details','service','memo','notes','po','wo','sku',
  'ticket','reservation','confirmation','document','statement','estimate',
  'quote','transaction','txn','model','serial','last','next',
]);

function firstWordIsHeader(raw: string): boolean {
  const first = raw.trim().split(/[\s:#]+/)[0];
  if (!first) return false;
  const norm = first.toLowerCase().replace(/[^a-z0-9]/g, '');
  return FIRST_WORD_HEADERS.has(norm);
}

// Street-address-y line: starts with a house number, or ends with common
// street suffixes / suite indicators. Helps when "Seller" → vendor name →
// address are interleaved oddly.
const VENDOR_SKIP_ADDRESS =
  /^\d+\s+\S|\b(road|rd\.?|street|st\.?|avenue|ave\.?|blvd\.?|drive|dr\.?|lane|ln\.?|court|ct\.?|way|highway|hwy\.?|parkway|pkwy\.?|suite|ste\.?|apt\.?|unit|po\s*box)\b|,\s*[A-Z]{2}\s+\d{5}/i;

// Time-of-day on its own line ("10:32 AM", "14:05"). Often near the top
// of register receipts and definitely not the vendor.
const VENDOR_SKIP_TIME = /^\d{1,2}:\d{2}(\s*[AaPp][Mm])?\s*$/;

// Document-chrome lines like "Page 1 of 3", "INVOICE", a bare date.
const VENDOR_SKIP_NOISE =
  /^(page\s+\d+|invoice|estimate|quote|statement|bill|receipt|warranty|manual|policy|sale\s+receipt)\s*$|^thank\s+you|with\s+receipt|may\s+be\s+returned|^returns?\b|non[- ]?refundable|^[\w\s&'-]{0,40}(report|record|summary|certificate|declarations?)\s*$/i;

// Canonical names for common US retailers/utilities whose OCR'd headers
// are stylized ("WAL*MART STORE #1234", "TGT T-1234") or all caps. Hits
// here trump the heuristic — vendor display becomes the canonical form.
// Keyed by lowercased substring (single match wins, first match in scan).
const CANONICAL_VENDORS: { match: RegExp; name: string }[] = [
  { match: /wal[\s*\-]?mart/i, name: 'Walmart' },
  { match: /\btarget\b|^tgt\b/i, name: 'Target' },
  { match: /costco/i, name: 'Costco' },
  { match: /sam'?s\s*club/i, name: "Sam's Club" },
  { match: /home\s*depot/i, name: 'The Home Depot' },
  { match: /\blowe'?s\b/i, name: "Lowe's" },
  { match: /menards/i, name: 'Menards' },
  { match: /ace\s*hardware/i, name: 'Ace Hardware' },
  { match: /harbor\s*freight/i, name: 'Harbor Freight' },
  { match: /tractor\s*supply/i, name: 'Tractor Supply' },
  { match: /best\s*buy/i, name: 'Best Buy' },
  { match: /amazon/i, name: 'Amazon' },
  { match: /trader\s*joe/i, name: "Trader Joe's" },
  { match: /whole\s*foods/i, name: 'Whole Foods' },
  { match: /kroger/i, name: 'Kroger' },
  { match: /safeway/i, name: 'Safeway' },
  { match: /publix/i, name: 'Publix' },
  { match: /\bcvs\b/i, name: 'CVS' },
  { match: /walgreens/i, name: 'Walgreens' },
  { match: /\bdte\b|detroit\s*edison/i, name: 'DTE Energy' },
  { match: /consumers\s*energy/i, name: 'Consumers Energy' },
  { match: /comcast|xfinity/i, name: 'Comcast / Xfinity' },
  { match: /verizon/i, name: 'Verizon' },
  { match: /at\s*&\s*t|at&t/i, name: 'AT&T' },
  { match: /t-?mobile/i, name: 'T-Mobile' },
  { match: /netflix/i, name: 'Netflix' },
  { match: /spotify/i, name: 'Spotify' },
  { match: /apple/i, name: 'Apple' },
  { match: /\bups\b/i, name: 'UPS' },
  { match: /fedex/i, name: 'FedEx' },
  { match: /labcorp|laboratory\s+corporation\s+of\s+america/i, name: 'Labcorp' },
  { match: /google\s+(voice|fi|one|workspace|cloud)/i, name: 'Google' },
  { match: /\bvercel\b/i, name: 'Vercel' },
  { match: /quest\s+diagnostics/i, name: 'Quest Diagnostics' },
  { match: /dunham'?s/i, name: "Dunham's Sports" },
  { match: /\bmeijer\b/i, name: 'Meijer' },
  { match: /\baldi\b/i, name: 'Aldi' },
];

function normalizeVendor(raw: string): string {
  for (const c of CANONICAL_VENDORS) {
    if (c.match.test(raw)) return c.name;
  }
  // Layout-preserving OCR glues the doc-type badge onto the name line
  // ("Blue River Plumbing Services INVOICE") — strip those suffixes.
  const deBadged = raw.replace(
    /\s+(SERVICE\s+RECEIPT|SALE\s+RECEIPT|RECEIPT|INVOICE|ESTIMATE|QUOTE|QUOTATION|WARRANTY|STATEMENT|BUDGET\s+PLAN|ANNUAL\s+SERVICE|SERVICE\s+AGREEMENT|DECLARATIONS?)\s*$/i,
    ''
  );
  // Tidy: collapse whitespace, strip trailing punctuation, cap length.
  return deBadged.replace(/\s+/g, ' ').replace(/[\s.,;:!*#-]+$/, '').slice(0, 60);
}

function detectVendor(lines: string[]): string | null {
  // Pass 1: scan the raw block for a canonical match — wins regardless of
  // line position because store names often appear in headers AND footers,
  // and a known-vendor signal is stronger than any positional heuristic.
  const headBlock = lines.slice(0, 20).join(' ');
  for (const c of CANONICAL_VENDORS) {
    if (c.match.test(headBlock)) return c.name;
  }

  // The homeowner's own name/address block often PRECEDES the business
  // on real paperwork (vet estimates, lab bills, mailed statements) —
  // lines inside a "Patient / Client / Bill to" window are never the
  // vendor (Wave 55: "JASON BALLANTYNE" became Labcorp's vendor).
  const partyRe =
    /^(patient|customer|client|member|caregiver|prepared\s+for|bill(ed)?\s+to|sold\s+to|ship\s+to|account\s+holder|insured|registered\s+owner|homeowner)\b/i;
  const blocked = new Set<number>();
  lines.slice(0, 14).forEach((l, i) => {
    if (partyRe.test(l)) {
      for (let j = i + 1; j <= i + 3; j++) blocked.add(j);
    }
  });

  // Pass 2: positional heuristic. We're conservative — a vendor must look
  // like a real business name, not a column heading. Requires multiple
  // words OR ≥ 6 letters AND no match against the single-word header
  // blacklist. Better to return null than to plant "Request" in the form.
  const candidates: { line: string; score: number }[] = [];
  const sliced = lines.slice(0, 12);
  for (let li = 0; li < sliced.length; li++) {
    const line = sliced[li];
    if (blocked.has(li)) continue;
    if (/^\d/.test(line)) continue;
    // Account-number suffixes mark a PERSON'S file, not a business:
    // "Theresa Ballantyne (# 22652)".
    if (/\(#?\s*\d+\s*\)/.test(line)) continue;
    // A line that is just a date ("September 7, 2026").
    if (/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{1,2},?\s+\d{4}$/i.test(line)) continue;
    if (VENDOR_SKIP_PREFIX.test(line)) continue;
    if (VENDOR_SKIP_ADDRESS.test(line)) continue;
    if (VENDOR_SKIP_TIME.test(line)) continue;
    if (VENDOR_SKIP_NOISE.test(line)) continue;
    if (/\d{5}(-\d{4})?$/.test(line)) continue;
    if (/@/.test(line)) continue;
    if (line.length > 60) continue;
    if (isLikelyHeaderToken(line)) continue;
    if (firstWordIsHeader(line)) continue;
    if (looksGibberish(line)) continue;
    // Document-type / field-label phrases are never business names
    // ("BUILDING PERMIT", "REBATE CONFIRMATION", "COVERAGE LIMIT
    // DEDUCTIBLE", "Inspection date: …").
    if (/\b(permit|inspection|confirmation|rebate|coverage|deductible|approved|result|sketch)\b|date:/i.test(line)) continue;

    const words = line.split(/\s+/).filter(Boolean);
    const letters = line.replace(/[^a-z]/gi, '');
    if (letters.length < 4) continue;
    // The strict rule: must be multi-word OR a long-enough single word.
    // "Walmart" (7 chars, 1 word) passes; "Request" (7) is blacklisted
    // above; "Inc" (3 chars) is blacklisted.
    if (words.length < 2 && letters.length < 6) continue;
    // Skip lines that are mostly numbers + punctuation.
    if (letters.length / line.length < 0.5) continue;

    let score = 0;
    // Multi-word business names are the most reliable signal.
    if (words.length >= 2) score += 5;
    if (line.length <= 30) score += 2;
    if (/^[A-Z0-9\s.&'-]{3,}$/.test(line)) score += 3;
    if (/\b(inc\.?|llc|l\.l\.c\.|corp\.?|co\.?|company|ltd|services?|energy|electric|gas|water|insurance|bank|credit\s*union|hospital|clinic|animal|veterinary|medical|dental|pharmacy|lab(orator(y|ies))?|corporation|automotive|auto|hardware|lumber|garden|nursery|plumbing|roofing|heating|cooling|landscap\w*|construction|builders?|contract\w*)\b/i.test(line)) score += 4;
    if (/\.com\b|\.net\b|\.org\b/i.test(line)) score += 2;
    if (line === lines[0]) score += 2;
    // Two plain capitalized words with no business vocabulary look
    // like a PERSON ("JASON BALLANTYNE") — heavily penalized so a real
    // company name elsewhere on the page outranks the addressee.
    if (
      words.length === 2 &&
      !/\d/.test(line) &&
      /^[A-Za-z]+\s+[A-Za-z]+$/.test(line) &&
      score <= 10
    ) {
      score -= 4;
    }
    candidates.push({ line, score });
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.score - a.score);
  // Minimum-confidence floor. Single-word non-canonical candidates only
  // hit ~4 max; the floor at 5 means a vendor must look like a real
  // multi-word business name OR carry a brand suffix (Inc/LLC/Co/Energy).
  // Less aggressive than returning null here would mean planting "Year"
  // or "Status" in the form — and the user has explicitly told us that's
  // unacceptable. Better to leave the field blank and let them type.
  if (candidates[0].score < 5) return null;
  return normalizeVendor(candidates[0].line);
}

// Heuristic for the "what did I buy?" line. Four passes, fastest wins:
//   1. "Description"/"Item"/"Product" header → next substantive line.
//   2. Line directly before the largest currency amount on the receipt
//      (true on single-item invoices: "Improved AJAX Login" → "$19.00").
//   3. Strip qty/SKU prefixes from a single substantive line if there's
//      only one in the body.
//   4. Longest meaningful body line — last resort.
function detectDescription(
  lines: string[],
  lineItems: { desc: string; amount_cents: number | null }[] = [],
  project: string | null = null
): string | null {
  // Pass 0 (Wave 53): a named project IS the description — estimates
  // say exactly what the job is ("Front foundation bed refresh…").
  if (project) return project;
  // Collection notices / dunning letters have NO work description —
  // every line is boilerplate, payment buttons, or the debtor's own
  // name. Give up early; the title falls back to the vendor, which is
  // exactly right for "Labcorp — $30.94 past due" (Wave 55).
  const whole = lines.join('\n');
  if (
    /\b(past\s+due|collection\s+(notice|agency)|immediate\s+payment\s+required|final\s+notice)\b/i.test(whole) &&
    !/\b(work\s+performed|description\s+of\s+(work|services))\b/i.test(whole)
  ) {
    return null;
  }
  // Pass 0.25: a labeled product line ("Product: AquaTherm ProLine
  // 50-gallon gas water heater") beats any heuristic — warranties and
  // registrations name the thing they cover.
  for (const line of lines) {
    const m = /^product\s*[:\-]\s*(.{4,120})$/i.exec(line);
    if (m) {
      const v = m[1].split('|')[0].trim().replace(/\s+/g, ' ');
      if (v.replace(/[^a-z]/gi, '').length >= 4) return v.slice(0, 80);
    }
  }
  // Pass 0.5: the biggest-ticket line item reads as "the work" on
  // invoices ("Install whole-home surge protector at main panel").
  if (lineItems.length > 0) {
    const top = [...lineItems].sort(
      (a, b) => (b.amount_cents ?? 0) - (a.amount_cents ?? 0)
    )[0];
    if (top.desc) return top.desc;
  }
  const headerRe = /^(description|item|product|details|service)s?\b/i;
  const columnNoiseRe = /^(quantity|qty|amount|price|unit|rate|hours|hrs|sku|item\s*#|line)\b/i;
  // Lines inside a "Customer / Bill to / Prepared for" block are the
  // HOMEOWNER's name and address — Vision's reading order can park
  // them right next to the totals, where the walkback pass used to
  // pick "Alex & Morgan Carter" as the work description (Wave 54.1).
  const partyLabelRe =
    /^(customer|client|prepared\s+for|bill(ed)?\s+to|sold\s+to|ship\s+to|registered\s+owner|service\s+address|property|homeowner)\b/i;
  const blocked = new Set<number>();
  for (let i = 0; i < lines.length; i++) {
    if (partyLabelRe.test(lines[i])) {
      for (let j = i + 1; j <= Math.min(lines.length - 1, i + 4); j++) blocked.add(j);
    }
  }
  const moneyRe = /\$\s?\d|^\d+\.\d{2}$|^\d+\s*\.\s*\d{2}$/;
  // Strip leading "1x ", "2 @ ", "Qty 3" prefixes that bleed into item lines.
  const qtyPrefix = /^(qty\s*)?\d+\s*(x|@)\s+/i;

  const isMeaningful = (s: string): boolean => {
    if (s.length < 4) return false;
    if (VENDOR_SKIP_PREFIX.test(s)) return false;
    if (VENDOR_SKIP_ADDRESS.test(s)) return false;
    if (VENDOR_SKIP_TIME.test(s)) return false;
    if (VENDOR_SKIP_NOISE.test(s)) return false;
    if (columnNoiseRe.test(s)) return false;
    if (moneyRe.test(s)) return false;
    if (/^\d+$/.test(s)) return false;
    if (/^[\d\W]+$/.test(s)) return false;
    if (/@|^https?:|^www\./i.test(s)) return false;
    if (isLikelyHeaderToken(s)) return false;
    if (firstWordIsHeader(s)) return false;
    if (looksGibberish(s)) return false;
    // "ESTIMATED TOTAL" / "Quoted subtotal" are money labels, never
    // the work description.
    if (/\b(sub)?total\b/i.test(s)) return false;
    // Card-terminal chrome, dunning labels, legal boilerplate, and
    // form-section headers (Wave 55, real-receipt testing): none of
    // these are ever what the money bought.
    if (/\b(network\s*id|appr(oval)?\s*code|auth\s*code|terminal|batch\s*#|trace\s*#|past\s+due|immediate\s+payment)\b/i.test(s)) return false;
    if (/\bI\s+(understand|agree|authorize|acknowledge)\b|by\s+signing|\bhereby\b/i.test(s)) return false;
    if (/\b(priced|listed|shown|described)\s+(below|above)\b/i.test(s)) return false;
    // A line that is only a date ("Friday, March 1st, 2019") or an id
    // label ("Billing ID") is never the work description.
    if (/^(monday|tuesday|wednesday|thursday|friday|saturday|sunday),?\s/i.test(s)) return false;
    if (/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{1,2}(st|nd|rd|th)?,?\s+\d{4}$/i.test(s)) return false;
    if (/\b(billing|account|invoice|payment)\s+id\b/i.test(s)) return false;
    // Bullet-point fine print, payment-method lines, remittance
    // instructions, and label lines ending in a colon.
    if (/^[•·*-]\s/.test(s)) return false;
    if (/:\s*$/.test(s)) return false;
    if (/payable\s+to|money\s+order|make\s+check/i.test(s)) return false;
    // Disclaimers ("Not insurance / not a warranty") describe what the
    // document ISN'T.
    if (/\bnot\s+(an?\s+)?(insurance|warranty|invoice|estimate|quote|contract)\b/i.test(s)) return false;
    // Mid-sentence fragments from wrapped letter text start lowercase
    // ("banking service must include") — item lines never do.
    if (/^[a-z]/.test(s)) return false;
    // Letter/marketing prose starts with function words ("We've made
    // it easy", "Your account is past due") — item and work lines
    // start with nouns, verbs, or product words.
    if (/^(we|we'?ve|we'?re|our|you|your|please|this|that|these|those|if|it|at|to|for|from|with|as|in|on|by|the|a|an)\s/i.test(s)) return false;
    // A bare ALL-CAPS person name ("JASON M BALLANTYNE") — 2-3 pure
    // letter words with no work vocabulary is an addressee, not a
    // description.
    if (
      /^[A-Z]{2,}(\s+[A-Z]\.?)?(\s+[A-Z]{2,})$/.test(s) &&
      !/(service|repair|install|clean|maintenance|removal|replacement|inspection|labor|supply|parts)/i.test(s)
    ) {
      return false;
    }
    if (/if\s+you\s+have\s+questions|contact\s+your\s+insurer|customer\s+service\s+at/i.test(s)) return false;
    if (/\b(debit|credit)\s*\/?\s*(credit\s+)?card\b/i.test(s)) return false;
    const letters = s.replace(/[^a-z]/gi, '');
    if (letters.length < 4) return false;
    // Must be multi-word OR contain at least 6 letters — same rule as
    // vendor scoring; prevents picking up "Year" / "Code" as a description.
    const words = s.split(/\s+/).filter(Boolean);
    if (words.length < 2 && letters.length < 6) return false;
    return true;
  };

  const clean = (s: string): string =>
    s.replace(qtyPrefix, '').replace(/\s+/g, ' ').trim().slice(0, 80);

  // Pass 1: anchored on a "Description" header.
  for (let i = 0; i < lines.length; i++) {
    if (!headerRe.test(lines[i])) continue;
    for (let j = i + 1; j < Math.min(lines.length, i + 8); j++) {
      if (!blocked.has(j) && isMeaningful(lines[j])) return clean(lines[j]);
    }
  }

  // Pass 2: line right before the largest currency amount on the receipt.
  // For invoice-style receipts with a clear "item — price" pair, this is
  // the strongest signal.
  let largestAt = -1;
  let largestVal = -1;
  for (let i = 0; i < lines.length; i++) {
    if (SUBTOTAL_BLOCK.test(lines[i]) || TOTAL_HINTS.test(lines[i])) continue;
    const v = pickLargest(lines[i]);
    if (v != null && v > largestVal) {
      largestVal = v;
      largestAt = i;
    }
  }
  if (largestAt > 0) {
    // Walk backwards from the price up to 4 lines to find a meaningful one.
    for (let j = largestAt - 1; j >= Math.max(0, largestAt - 4); j--) {
      if (!blocked.has(j) && isMeaningful(lines[j])) return clean(lines[j]);
    }
  }

  // Pass 3 + 4: longest meaningful body line (skip header band + tail).
  const start = Math.min(4, Math.floor(lines.length * 0.15));
  const end = Math.max(start, Math.floor(lines.length * 0.85));
  // Desperation passes get a QUALITY FLOOR (Wave 55): item names are
  // short; long prose lines here are always letter boilerplate ("En
  // este momento, su cuenta esta delincuente…"). Better to return
  // null and let the title fall back to the vendor name.
  const body = lines
    .slice(start, end)
    .filter(
      (l, i) =>
        !blocked.has(start + i) &&
        isMeaningful(l) &&
        l.length <= 60 &&
        l.split(/\s+/).length <= 7
    );
  if (body.length === 0) return null;
  body.sort((a, b) => b.length - a.length);
  return clean(body[0]);
}

function detectAmountCents(lines: string[]): number | null {
  // Pass 1: look for a line containing a "total" hint (but not subtotal/tax)
  //          and extract the largest currency value from it.
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    if (!TOTAL_HINTS.test(line)) continue;
    // "Amount due" / "Balance due" / "Grand total" are the strongest
    // possible signals — don't let the generic 'due' blocklist entry
    // (meant for "Due: 09/27" date lines) veto them.
    const strongTotal = /\b(amount|balance)\s+due\b|\bgrand\s+total\b/i.test(line);
    if (SUBTOTAL_BLOCK.test(line) && !strongTotal) continue;
    // Register receipts often put "TOTAL" and its value on separate
    // OCR lines. Only trust the NEXT line when this one is a short
    // standalone total label — a "Qty Rate Amount" column header also
    // matches the hint but its next line is a line item, not the total.
    const standaloneLabel =
      line.length <= 22 && /^(grand\s+)?total\b|^amount\s+due\b|^balance\s+due\b/i.test(line);
    let amount = pickLargest(line);
    if (amount == null && standaloneLabel) {
      // Column-scrambled OCR (angled photos): the label block and the
      // value block separate — "Total Invoice: $" is followed by a RUN
      // of bare numbers where the invoice total is the LARGEST, not
      // the first (Wave 55: a vet estimate read $5.15 instead of
      // $298.50 this way).
      let runMax: number | null = null;
      for (let j = i + 1; j <= Math.min(lines.length - 1, i + 8); j++) {
        const next = lines[j];
        if (next.length > 24) break;
        const v = pickLargest(next);
        if (v == null) break;
        if (runMax == null || v > runMax) runMax = v;
      }
      amount = runMax;
    }
    if (amount != null) return amount;
  }
  // Pass 1.5 (Wave 53): no "Total" line at all — paid-in-full receipts
  // often end "Subtotal $188 / Payment -$188 / Balance $0.00". The
  // subtotal IS the amount then; without this the largest line item
  // wins and undercounts.
  for (let i = lines.length - 1; i >= 0; i--) {
    if (!/^sub-?total\b/i.test(lines[i])) continue;
    const amount = pickLargest(lines[i]) ?? pickLargest(lines[i + 1] ?? '');
    if (amount != null) return amount;
  }
  // Pass 2: look at the largest currency value anywhere in the last third
  //          of the receipt (totals are almost always near the bottom).
  const tail = lines.slice(Math.floor(lines.length * 0.6));
  const candidates: number[] = [];
  for (const line of tail) {
    if (SUBTOTAL_BLOCK.test(line) && !TOTAL_HINTS.test(line)) continue;
    const v = pickLargest(line);
    if (v != null) candidates.push(v);
  }
  if (candidates.length > 0) return Math.max(...candidates);
  // Pass 3: largest currency value anywhere.
  const all: number[] = [];
  for (const line of lines) {
    const v = pickLargest(line);
    if (v != null) all.push(v);
  }
  return all.length > 0 ? Math.max(...all) : null;
}

// Dollar-prefixed WHOLE amounts ("$773", "$773 USD") — invoice/quote
// totals often print without cents. The $ prefix keeps quantities,
// dates, and reference numbers from matching.
const MONEY_INT_RE = /\$\s?(\d{1,3}(?:,\d{3})*)(?!\.?\d)/g;

function pickLargest(line: string): number | null {
  const values: number[] = [];
  let m: RegExpExecArray | null;
  MONEY_RE.lastIndex = 0;
  while ((m = MONEY_RE.exec(line)) !== null) {
    const cents = Math.round(parseFloat(m[1].replace(/,/g, '')) * 100);
    if (!Number.isFinite(cents) || cents <= 0) continue;
    values.push(cents);
  }
  MONEY_INT_RE.lastIndex = 0;
  while ((m = MONEY_INT_RE.exec(line)) !== null) {
    const cents = Math.round(parseFloat(m[1].replace(/,/g, '')) * 100);
    if (!Number.isFinite(cents) || cents <= 0) continue;
    values.push(cents);
  }
  return values.length > 0 ? Math.max(...values) : null;
}

function detectDate(text: string): number | null {
  // Earliest-position match wins across ALL formats (Wave 55): a vet
  // estimate dated "Apr 23, 2026" also lists "Flea & Tick Prevention:
  // 05/23/2026" later — trying the numeric regex first over the whole
  // text picked the future reminder date over the document date.
  let best: { at: number; when: number } | null = null;
  for (const re of DATE_RES) {
    const m = re.exec(text);
    if (!m) continue;
    const d = parseMatch(m);
    if (!d || Number.isNaN(d.getTime())) continue;
    if (!best || (m.index ?? 0) < best.at) best = { at: m.index ?? 0, when: d.getTime() };
  }
  return best?.when ?? null;
}

function parseMatch(m: RegExpExecArray): Date | null {
  const a = m[1], b = m[2], c = m[3];
  const monthAbbrev: Record<string, number> = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  };
  const monthIdx = monthAbbrev[a.toLowerCase().slice(0, 3)];
  if (monthIdx != null) {
    // "Jan 15, 2026"
    return new Date(parseInt(c, 10), monthIdx, parseInt(b, 10));
  }
  if (a.length === 4) {
    // "2026-01-15"
    return new Date(parseInt(a, 10), parseInt(b, 10) - 1, parseInt(c, 10));
  }
  // "1/15/26" or "1/15/2026" — US-style assumed
  const year = c.length === 2 ? 2000 + parseInt(c, 10) : parseInt(c, 10);
  return new Date(year, parseInt(a, 10) - 1, parseInt(b, 10));
}
